import { MapContextMenuItem } from "./../MapContextMenuItem";
import { SelectedFeatureInfo } from "./../SelectedFeatureInfo";
import { ContextMenu } from "./../../components/utils/context-menu";
import "ol/ol.css";
import { Map as OpenlayerMap } from "ol";
import MapBrowserEvent from "ol/MapBrowserEvent";
import { Pointer } from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { transform } from "ol/proj";
import { ITraVosPlayEvent } from "../layerFactory/ITraVosPlayEvent";
import { GeoMap } from "../GeoMap";
import { ICommonView } from "@/components/pages/common/ICommonView";
import { MessageDefine } from "@/components/pages/common/MessageDefine";
import { Image } from "ol/layer";
import ImageWMSEx from "../net/ImageWMSEx";
import { ServerError } from "../define/ServerError";
import { Pixel } from "ol/pixel";
import { featureGroup } from "../util/geoJsonData";

/**
 * マウスダウンイベント定義値
 */
const EVNET_MOUSE_TYPE_DOWN = "pointerdown";

/**
 * マウスアップイベント定義値
 */
const EVENT_MOUSE_TYPE_UP = "pointerup";

/**
 * マウス左クリックボタン定義値
 */
const EVENT_MOUSE_LEFT_BUTTON = 0;

/**
 * マウス右クリックボタン定義値
 */
const EVENT_MOUSE_RIGHT_BUTTON = 2;

/**
 * マウスダウン地点ピクセル
 */
let downPointPixel: Pixel;

/**
 * 地物選択・ドラッグ閾値
 */
const SELECT_DRAG_THRESHOLD = 2;

/**
 * Traovosレイヤプレフィックス
 */
const TRAVOS_PREFIX = "v_travos";

/**
 * 地物情報選択処理クラス
 */
export class FeatureGetInfoInteraction {
  /**
   * マウスイベント操作用のInteraction
   */
  private _pointer: Pointer;

  /**
   * 地図クラス
   */
  private _map: OpenlayerMap;

  /**
   * コンテキストメニュー表示用
   */
  private _contextMenu: ContextMenu;

  /**
   * 地図共通クラスIF
   */
  private _geoMap: GeoMap;

  /**
   * 地物選択時コールバック関数
   */
  private _selectedFeatureAttribute: (
    featureAttribute: SelectedFeatureInfo
  ) => boolean;

  /**
   * TraVos再生イベント
   */
  public _traVosPlayEvent: ITraVosPlayEvent;

  /**
   * 地物選択用データソース
   */
  private _selectedFeatureSource: VectorSource;

  /**
   * 共通処理
   */
  private _commonView: ICommonView;

  /**
   * 選択WMSレイヤ
   */
  private _selectedFeatureWmsLayer: Image<ImageWMSEx>;

  private _selectedWmsLayerParam = new Array<{
    layerName: string;
    selectedFid: string;
    selectedStyle: string;
  }>();

  /**
   * 選択TravosWMSレイヤ
   */
  private _selectedFeatureTravosWmsLayer: Image<ImageWMSEx>;

  /**
   * 選択TravosWMSレイヤ用パラメータ
   */
  private _selectedTravosWmsLayerParam = new Array<{
    layerName: string;
    selectedFid: string;
    cqlParameter: string;
  }>();

  /**
   * WMSレイヤ
   */
  private _featureWmsLayer: Image<ImageWMSEx>;

  private _wmsLayerParam = new Array<{
    layerName: string;
    selectedFid: string;
  }>();

  /**
   * ID変換用マップ
   */
  private _idMap: Map<string, string> = new Map();

  /**
   * コンストラクタ
   * @param map 地図クラス
   * @param selectedFeatureLayer 地物選択用レイヤ
   * @param geoMap 地図共通コントロールクラス
   */
  public constructor(
    map: OpenlayerMap,
    selectedFeatureLayer: VectorLayer<VectorSource>,
    selectedFeatureWmsLayer: Image<ImageWMSEx>,
    selecedFeatureTravosWmsLayer: Image<ImageWMSEx>,
    featureWmsLayer: Image<ImageWMSEx>,
    contextMenu: ContextMenu,
    geoMap: GeoMap,
    commonView: ICommonView
  ) {
    this._map = map;
    this._geoMap = geoMap;
    this._contextMenu = contextMenu;
    this._selectedFeatureSource = selectedFeatureLayer.getSource();
    this._commonView = commonView;
    this._selectedFeatureWmsLayer = selectedFeatureWmsLayer;
    this._selectedFeatureTravosWmsLayer = selecedFeatureTravosWmsLayer;
    this._featureWmsLayer = featureWmsLayer;
  }

  /**
   * 地物選択開始
   * @param selectedFeatureAttribute 地物選択コールバック
   */
  public startSelect(
    selectedFeatureAttribute: (
      featureAttribute: SelectedFeatureInfo
    ) => boolean,
    traVosPlayEvent: ITraVosPlayEvent
  ) {
    this._selectedFeatureAttribute = selectedFeatureAttribute;
    this._traVosPlayEvent = traVosPlayEvent;
    this._pointer = new Pointer({
      handleEvent: this.drawHandleEvent.bind(this),
    });

    this._map.addInteraction(this._pointer);
  }

  /**
   * 地物選択終了
   */
  public endSelect() {
    this._map.removeInteraction(this._pointer);
  }

  /*
   * Pointerから呼び出されるマウスイベント処理
   * マウスボタン押下時に選択箇所の地物を取得し、取得件数に応じた処理を行う。
   * @param evt 地図上のマウスイベント
   * @returns
   */
  private drawHandleEvent(evt: MapBrowserEvent<any>): boolean {
    if (evt.type == EVNET_MOUSE_TYPE_DOWN) {
      if (evt.originalEvent.button == EVENT_MOUSE_LEFT_BUTTON) {
        downPointPixel = evt.pixel;
      } else if (evt.originalEvent.button == EVENT_MOUSE_RIGHT_BUTTON) {
        this._contextMenu.close();
        this.getRightClickInfoList(evt).then(
          (res: Array<MapContextMenuItem>) => {
            if (res.length === 0) {
              return true;
            } else {
              let baseMenuName = "";
              let menuIndex = 0;
              const menuMap = new Map<string, boolean>();
              res.forEach((mapContextMenuItem) => {
                if (menuMap.has(mapContextMenuItem.contextMenuName)) {
                  menuMap.delete(mapContextMenuItem.contextMenuName);
                  menuMap.set(mapContextMenuItem.contextMenuName, true);
                } else {
                  menuMap.set(mapContextMenuItem.contextMenuName, false);
                }
              });
              res.forEach((mapContextMenuItem) => {
                if (menuMap.get(mapContextMenuItem.contextMenuName)) {
                  if (baseMenuName != mapContextMenuItem.contextMenuName) {
                    baseMenuName = mapContextMenuItem.contextMenuName;
                  }
                  menuIndex++;
                  mapContextMenuItem.contextMenuName =
                    baseMenuName + "(" + menuIndex + ")";
                }
                if (this._contextMenu._isShow) {
                  this._contextMenu.close();
                }
                this._contextMenu.addMenuItem(
                  mapContextMenuItem.contextMenuName,
                  () => {
                    mapContextMenuItem.contextClickCallBack();
                    if (
                      mapContextMenuItem.selectedFeature &&
                      !mapContextMenuItem.isDelete
                    ) {
                      this._selectedFeatureSource.addFeature(
                        mapContextMenuItem.selectedFeature
                      );
                    }
                  },
                  mapContextMenuItem.id
                );
              });
            }
            this._contextMenu.show(evt.coordinate);
          }
        );
      }
    } else if (
      evt.originalEvent.button == EVENT_MOUSE_LEFT_BUTTON &&
      evt.type == EVENT_MOUSE_TYPE_UP
    ) {
      if (
        downPointPixel &&
        (downPointPixel[0] - SELECT_DRAG_THRESHOLD > evt.pixel[0] ||
          downPointPixel[0] + SELECT_DRAG_THRESHOLD < evt.pixel[0] ||
          downPointPixel[1] - SELECT_DRAG_THRESHOLD > evt.pixel[1] ||
          downPointPixel[1] + SELECT_DRAG_THRESHOLD < evt.pixel[1])
      ) {
        return true;
      }
      if (evt.originalEvent.target.id.includes("contextMenuItem")) {
        return true;
      }
      this._contextMenu.close();
      this.getFeatureInfoList(evt).then((res) => {
        if (!res) {
          return true;
        }
        const selectedFeatureInfoList = res?.filter((ele) => {
          return ele != null;
        });
        if (selectedFeatureInfoList.length == 0) {
          //選択結果が0件の場合は処理せず終了
          return true;
        } else if (selectedFeatureInfoList.length == 1) {
          if (this._idMap.get(selectedFeatureInfoList[0].featureGeoJson.fid)) {
            return;
          }
          if (selectedFeatureInfoList[0].wmsFid) {
            //WMS選択の場合の選択処理
            if (
              selectedFeatureInfoList[0].wmsLayerName?.startsWith(TRAVOS_PREFIX)
            ) {
              this.selectedTravosWmsFeature(
                selectedFeatureInfoList[0].wmsLayerName,
                selectedFeatureInfoList[0].wmsFid,
                selectedFeatureInfoList[0].cqlParameter
              );
              setTimeout(() => {
                this._selectedFeatureTravosWmsLayer.setOpacity(1);
              }, 100);
            } else {
              this.selectedWmsFeature(
                selectedFeatureInfoList[0].wmsLayerName,
                selectedFeatureInfoList[0].wmsFid,
                selectedFeatureInfoList[0].wmsStyle
              );
              setTimeout(() => {
                this._selectedFeatureWmsLayer.setOpacity(1);
              }, 100);
              this._wmsLayerParam.push({
                layerName: selectedFeatureInfoList[0].wmsLayerName,
                selectedFid: selectedFeatureInfoList[0].wmsFid,
              });
              this.updateWmsFeature();
              setTimeout(() => {
                this._featureWmsLayer.setOpacity(1);
              }, 100);
            }
            this._idMap.set(
              selectedFeatureInfoList[0].featureGeoJson.fid,
              selectedFeatureInfoList[0].wmsFid
            );
          }
          if (!selectedFeatureInfoList[0].isSelectedFeatureWms) {
            selectedFeatureInfoList[0].selectFeature.setId(
              selectedFeatureInfoList[0].featureGeoJson.fid
            );
            if (selectedFeatureInfoList[0].groupIndex != undefined) {
              featureGroup[selectedFeatureInfoList[0].groupIndex].forEach(
                (coordinate, index) => {
                  const tmpFeature =
                    selectedFeatureInfoList[0].selectFeature.clone();
                  tmpFeature.setId(
                    selectedFeatureInfoList[0].selectFeature.getId() +
                      "_" +
                      index.toString()
                  );
                  (tmpFeature.getGeometry() as any).setCenter(coordinate);
                  this._selectedFeatureSource.addFeature(tmpFeature);
                }
              );
            } else {
              this._selectedFeatureSource.addFeature(
                selectedFeatureInfoList[0].selectFeature
              );
            }
          }
          const isOpened = this._selectedFeatureAttribute(
            selectedFeatureInfoList[0]
          );
          if (isOpened && selectedFeatureInfoList[0].clickMenuItemAction) {
            selectedFeatureInfoList[0].clickMenuItemAction();
          }
        } else if (selectedFeatureInfoList.length > 1) {
          let baseMenuName = "";
          let menuIndex = 1;
          const menuMap = new Map<string, boolean>();
          selectedFeatureInfoList.forEach((selectedFeatureInfo) => {
            if (menuMap.has(selectedFeatureInfo.contextMenuName)) {
              menuMap.delete(selectedFeatureInfo.contextMenuName);
              menuMap.set(selectedFeatureInfo.contextMenuName, true);
            } else {
              menuMap.set(selectedFeatureInfo.contextMenuName, false);
            }
          });

          for (const value of selectedFeatureInfoList) {
            if (this._idMap.get(value.featureGeoJson.fid)) {
              continue;
            }
            const hasMenus = menuMap.get(value.contextMenuName);
            if (hasMenus) {
              if (baseMenuName == value.contextMenuName) {
                menuIndex = menuIndex + 1;
              } else {
                baseMenuName = value.contextMenuName;
                menuIndex = 1;
              }
              value.contextMenuName = baseMenuName + "(" + menuIndex + ")";
            }
            if (this._contextMenu._isShow) {
              this._contextMenu.close();
            }
            this._contextMenu.addMenuItem(value.contextMenuName, () => {
              //メニュー選択時は選択した情報を地物情報選択処理コールバックに通知
              if (value.wmsFid) {
                //WMS選択の場合の選択処理
                if (value.wmsLayerName?.startsWith(TRAVOS_PREFIX)) {
                  this.selectedTravosWmsFeature(
                    value.wmsLayerName,
                    value.wmsFid,
                    value.cqlParameter
                  );
                  setTimeout(() => {
                    this._selectedFeatureTravosWmsLayer.setOpacity(1);
                  }, 100);
                } else {
                  this.selectedWmsFeature(
                    value.wmsLayerName,
                    value.wmsFid,
                    value.wmsStyle
                  );
                  setTimeout(() => {
                    this._selectedFeatureWmsLayer.setOpacity(1);
                  }, 100);
                  this._wmsLayerParam.push({
                    layerName: value.wmsLayerName,
                    selectedFid: value.wmsFid,
                  });
                  this.updateWmsFeature();
                  setTimeout(() => {
                    this._featureWmsLayer.setOpacity(1);
                  }, 100);
                }
                this._idMap.set(
                  value.featureGeoJson.fid,
                  value.wmsFid ? value.wmsFid : value.featureGeoJson.fid
                );
              }
              if (!value.isSelectedFeatureWms) {
                value.selectFeature.setId(value.featureGeoJson.fid);
                if (value.groupIndex != undefined) {
                  featureGroup[value.groupIndex].forEach(
                    (coordinate, index) => {
                      const tmpFeature = value.selectFeature.clone();
                      tmpFeature.setId(
                        value.selectFeature.getId() + "_" + index.toString()
                      );
                      (tmpFeature.getGeometry() as any).setCenter(coordinate);
                      this._selectedFeatureSource.addFeature(tmpFeature);
                    }
                  );
                } else {
                  this._selectedFeatureSource.addFeature(value.selectFeature);
                }
              }
              const isOpened = this._selectedFeatureAttribute(value);
              if (isOpened && value.clickMenuItemAction) {
                value.clickMenuItemAction();
              }
            });
          }
          this._contextMenu.show(evt.coordinate);
        }
      });
    }
    return true;
  }

  /**
   * 地物情報リストを取得する。
   * @param evt マウスクリックイベント
   */
  private async getFeatureInfoList(evt: MapBrowserEvent<any>) {
    const lonLat = transform(
      evt.coordinate,
      this._map.getView().getProjection(),
      "EPSG:4326"
    );
    const mapSize = this._map.getSize();
    const layerFactory = this._geoMap
      .getLayerFactory()
      .getLayerFactory()
      .reverse();
    let selectedFeatureInfoList: SelectedFeatureInfo[] = [];
    for (const targetLayerFactory of layerFactory) {
      if (
        targetLayerFactory.isFeatureInfo() &&
        targetLayerFactory.getVisibleLayerCount()
      ) {
        await targetLayerFactory
          .getFeatureInfo(
            this._map,
            lonLat[1],
            lonLat[0],
            Math.floor(evt.pixel[0]),
            Math.floor(evt.pixel[1]),
            mapSize[0],
            mapSize[1],
            this._map.getView().calculateExtent(mapSize)
          )
          .then((res: Array<SelectedFeatureInfo>) => {
            selectedFeatureInfoList = selectedFeatureInfoList.concat(
              res?.filter((ele) => {
                return ele !== undefined;
              })
            );
          })
          .catch((e) => {
            if (e instanceof ServerError) {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0001.getMessage([e._url, e._status])
              );
            } else {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0002.getMessage([e])
              );
            }
          });
      }
    }
    return selectedFeatureInfoList;
  }

  /**
   * 右クリックされた際の処理を行う。
   * @param evt マウスクリックイベント
   */
  private async getRightClickInfoList(evt: MapBrowserEvent<any>) {
    const lonLat = transform(
      evt.coordinate,
      this._map.getView().getProjection(),
      "EPSG:4326"
    );
    const mapSize = this._map.getSize();
    const layerFactory = this._geoMap.getLayerFactory().getLayerFactory();
    let rightClickInfoList: Array<MapContextMenuItem> = [];
    for (const targetLayerFactory of layerFactory) {
      if (
        targetLayerFactory.isRightClickInfo() &&
        targetLayerFactory.getVisibleLayerCount()
      ) {
        await targetLayerFactory
          .getRightClickInfo(
            this._map,
            lonLat[1],
            lonLat[0],
            Math.floor(evt.pixel[0]),
            Math.floor(evt.pixel[1]),
            mapSize[0],
            mapSize[1],
            this._map.getView().calculateExtent(mapSize)
          )
          .then((res: MapContextMenuItem[]) => {
            if (res) {
              rightClickInfoList = rightClickInfoList.concat(res);
            }
          })
          .catch((e) => {
            if (e instanceof ServerError) {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0001.getMessage([e._url, e._status])
              );
            } else {
              this._commonView._messageLogic.onOpenMessage(
                MessageDefine.E0002.getMessage([e])
              );
            }
          });
      }
    }
    return rightClickInfoList;
  }

  /**
   * WMS選択地物を表示する。
   * @param layerName レイヤ名
   * @param selectedFid 選択FID
   * @param selectedStyle 選択時スタイル
   */
  private selectedWmsFeature(
    layerName: string,
    selectedFid: string,
    selectedStyle: string
  ) {
    this._selectedWmsLayerParam.push({
      layerName: layerName,
      selectedFid: selectedFid,
      selectedStyle: selectedStyle,
    });
    this.updateSelectedWmsFeature();
  }

  /**
   * 選択WMSレイヤを更新する。
   */
  private updateSelectedWmsFeature() {
    const layerList = new Array<string>();
    const styleList = new Array<string>();
    const fidList = new Array<string>();
    this._selectedWmsLayerParam.forEach((element) => {
      layerList.push(element.layerName);
      styleList.push(element.selectedStyle);
      fidList.push(element.selectedFid);
    });
    this._selectedFeatureWmsLayer.getSource().updateParams({
      LAYERS: layerList.join(","),
      featureId: fidList.join(","),
      STYLES: styleList.join(","),
    });
  }

  /**
   * 選択TravosWMSレイヤのパラメータを追加する。
   */
  private selectedTravosWmsFeature(
    layerName: string,
    selectedFid: string,
    cqlParameter: string
  ) {
    this._selectedTravosWmsLayerParam.push({
      layerName: layerName,
      selectedFid: selectedFid,
      cqlParameter: cqlParameter,
    });
    this.updateSelectedTravosWmsFeature();
  }

  /**
   * 選択TravosWMSレイヤを更新する。
   */
  private updateSelectedTravosWmsFeature() {
    let layerList = new Array<string>();
    let cqlParameter = "";
    this._selectedTravosWmsLayerParam.forEach((element) => {
      layerList.push(element.layerName);
      if (cqlParameter !== "") {
        cqlParameter += " OR ";
      }
      cqlParameter += "(";
      cqlParameter += element.cqlParameter;
      cqlParameter += ")";
    });
    layerList = [...new Set(layerList)];
    this._selectedFeatureTravosWmsLayer.getSource().updateParams({
      LAYERS: layerList.join(","),
      CQL_FILTER: cqlParameter,
    });
  }

  /**
   * WMSレイヤを更新する。
   */
  private updateWmsFeature() {
    const layerList = new Array<string>();
    const fidList = new Array<string>();
    this._wmsLayerParam.forEach((element) => {
      layerList.push(element.layerName);
      fidList.push(element.selectedFid);
    });
    this._featureWmsLayer.getSource().updateParams({
      LAYERS: layerList.join(","),
      featureId: fidList.join(","),
    });
  }

  /**
   * 選択状態を解除する。
   * @param  地物ID
   */
  public unSelectedFeature(inputId: string) {
    const featureId = this._idMap.get(inputId);
    const selectedFeature = this._selectedFeatureSource.getFeatureById(inputId);
    if (selectedFeature) {
      this._selectedFeatureSource.removeFeature(selectedFeature);
    } else {
      this._selectedFeatureSource.getFeatures().forEach((feature) => {
        if (
          feature
            .getId()
            .toString()
            .startsWith(inputId + "_")
        ) {
          this._selectedFeatureSource.removeFeature(feature);
        }
      });
    }
    const selectedStartCnt = this._selectedWmsLayerParam.length;
    this._selectedWmsLayerParam = this._selectedWmsLayerParam?.filter(
      (value) => value.selectedFid != featureId && value.selectedFid != inputId
    );
    if (this._selectedWmsLayerParam.length === 0) {
      this._selectedFeatureWmsLayer.setOpacity(Number.MIN_VALUE);
    }
    if (selectedStartCnt != this._selectedWmsLayerParam.length) {
      this.updateSelectedWmsFeature();
    }
    const travosStartCnt = this._selectedTravosWmsLayerParam.length;
    this._selectedTravosWmsLayerParam =
      this._selectedTravosWmsLayerParam?.filter(
        (value) =>
          value.selectedFid != featureId && value.selectedFid != inputId
      );
    if (this._selectedTravosWmsLayerParam.length === 0) {
      this._selectedFeatureTravosWmsLayer.setOpacity(Number.MIN_VALUE);
    }
    if (travosStartCnt != this._selectedTravosWmsLayerParam.length) {
      this.updateSelectedTravosWmsFeature();
    }
    const startCnt = this._wmsLayerParam.length;
    this._wmsLayerParam = this._wmsLayerParam?.filter(
      (value) => value.selectedFid != featureId && value.selectedFid != inputId
    );
    if (this._wmsLayerParam.length === 0) {
      this._featureWmsLayer.setOpacity(Number.MIN_VALUE);
    }
    if (startCnt != this._wmsLayerParam.length) {
      this.updateWmsFeature();
    }
    if (featureId) {
      this._idMap.delete(inputId);
    }
  }
}
